import { useEffect, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { User, UserPlus, X, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { UserService } from "@/services/users/UserService";
import { TeamMember, Roles } from '@/types/auth/auth';
import { supabase } from "@/integrations/supabase/client";

interface TeamMembersSectionProps {
  onTeamUpdate?: () => void;
}

export const TeamMembersSection = ({ onTeamUpdate }: TeamMembersSectionProps) => {
  const { user, userProfile, team, refreshTeam } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  useEffect(() => {
    if (team) {
      loadTeamMembers();
    } else {
      setTeamMembers([]);
    }
  }, [team]);

  useEffect(() => {
    if (!team && userProfile?.full_name) {
      setNewTeamName(`${userProfile.full_name} Workspace`);
    }
  }, [team, userProfile?.full_name]);

  const loadTeamMembers = async () => {
    if (!team) return;

    try {
      // Get actual team members from database
      const teamData = await UserService.getTeamData(team.id, team.owner_id);

      if (!teamData) {
        setTeamMembers([]);
        return;
      }

      // Get pending invitations from database
      const { data: invitations, error: inviteError } = await supabase
        .from("team_invitations")
        .select("*")
        .eq("team_id", team.id)
        .eq("status", "pending");

      if (inviteError) {
        console.error("Error loading invitations:", inviteError);
      }

      // Combine actual members with pending invitations
      const allMembers: TeamMember[] = [
        ...teamData.members,
        ...(invitations || []).map(invite => ({
          id: invite.id,
          full_name: null,
          email: invite.email,
          avatar_url: null,
          role: "Invited" as const,
        }))
      ];

      setTeamMembers(allMembers);
    } catch (err) {
      console.error("Error loading team members:", err);
      setTeamMembers([]);
    }
  };

  const handleInviteTeamMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!inviteEmail.trim() || !/^\S+@\S+\.\S+$/.test(inviteEmail)) {
      toast.error("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    if (!team && !newTeamName.trim()) {
      toast.error("Please enter a team name.");
      setLoading(false);
      return;
    }

    if (!user?.id) {
      toast.error("User not found.");
      setLoading(false);
      return;
    }

    const loadingToast = toast.loading(`Sending invitation to ${inviteEmail}...`);

    try {
      let activeTeam = team;

      if (!activeTeam) {
        activeTeam = await UserService.createTeam(newTeamName, userProfile?.full_name || newTeamName, user.id);
        await refreshTeam();
        onTeamUpdate?.();
      }

      await UserService.inviteMember(
        inviteEmail,
        activeTeam,
        userProfile,
        user.id
      );

      // Reload team members to show the new invitation
      await loadTeamMembers();

      toast.success(`Invitation sent successfully! ${inviteEmail} will receive an email to join your team.`, {
        id: loadingToast,
        duration: 5000
      });
      setInviteEmail("");
    } catch (err: any) {
      console.error("Error sending invitation:", err);
      toast.error(err.message || "Failed to send invitation", { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (invitationId: string, email: string) => {
    if (!team) return;

    setResendingId(invitationId);
    const loadingToast = toast.loading(`Resending invitation to ${email}...`);

    try {
      await UserService.resendInvitation(invitationId, team.id, email, userProfile);
      toast.success(`Invitation resent to ${email}`, { id: loadingToast });
    } catch (err: any) {
      console.error("Error resending invitation:", err);
      toast.error(err.message || "Failed to resend invitation", { id: loadingToast });
    } finally {
      setResendingId(null);
    }
  };

  const handleRemoveMember = async (memberId: string, email: string | null, role: string) => {
    if (!team || !user) return;

    const loadingToast = toast.loading(
      role === "Invited"
        ? `Cancelling invitation for ${email}...`
        : `Removing ${email} from team...`
    );

    try {
      if (role === "Invited") {
        // Cancel invitation
        await UserService.cancelInvitation(memberId, team.id);
        toast.success(`Invitation cancelled for ${email}`, { id: loadingToast });
      } else {
        // Remove actual member
        await UserService.removeTeamMember(team.id, memberId, user.id);
        toast.success(`${email} has been removed from the team`, { id: loadingToast });
      }

      // Reload team members
      await loadTeamMembers();
    } catch (err: any) {
      console.error("Error removing member:", err);
      toast.error(err.message || "Failed to remove member", { id: loadingToast });
    }
  };

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center mb-[18px]">
        <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
          <UserPlus className="h-5 w-5 text-genie-primary" />
        </div>
        <h2 className="text-xl font-semibold">Team Members</h2>
      </div>
      <p className="text-sm text-gray-600 mb-[18px]">
        Team members will be able to view and edit genies created under this account.
      </p>
      <div className="mb-[18px] flex flex-col gap-2">
        {teamMembers.length === 0 ? (
          <p className="text-sm text-gray-500">No team members yet. Invite someone to get started!</p>
        ) : (
          teamMembers.map((member) => (
            <div key={member.id} className="flex justify-between items-center p-1.5 bg-[#F9FAFC] rounded-md">
              <div className="flex items-center gap-2">
                {member.avatar_url ? (
                  <img src={member.avatar_url} alt={member.full_name || member.email || 'User'} className="h-6 w-6 rounded-full object-cover border border-gray-200" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="h-3 w-3 text-gray-400" />
                  </div>
                )}
                <span className="text-sm">
                  <CopyableEmail email={member.email} fallback="Unknown User" />
                  {member.full_name && ` (${member.full_name})`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#555] bg-[#E5EAF0] px-1.5 py-[3px] rounded text-[0.7rem]">{member.role}</span>
                {member.role === "Invited" && team?.owner_id === user?.id && (
                  <button
                    onClick={() => handleResendInvite(member.id, member.email || "")}
                    disabled={resendingId === member.id}
                    className="text-genie-primary hover:text-genie-primary/80 p-1 rounded hover:bg-genie-primary/10 disabled:opacity-50"
                    title="Resend invitation"
                  >
                    {resendingId === member.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RotateCw className="h-3 w-3" />
                    )}
                  </button>
                )}
                {member.role !== "Owner" && team?.owner_id === user?.id && (
                  <button
                    onClick={() => handleRemoveMember(member.id, member.email, member.role)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                    title={member.role === "Invited" ? "Cancel invitation" : "Remove member"}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {(team ? team.owner_id === user?.id : userProfile?.role === Roles.Admin || userProfile?.role === Roles.Business) && (
        <form onSubmit={handleInviteTeamMember} className="flex flex-col gap-3">
          {!team && (
            <div>
              <label htmlFor="new-team-name" className="block mb-1.5 font-medium text-sm">
                Team Name
              </label>
              <input
                id="new-team-name"
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter team name"
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
                required
              />
            </div>
          )}
          <div className="flex gap-3 items-end">
            <div className="flex-grow">
              <label htmlFor="invite-email" className="block mb-1.5 font-medium text-sm">
                Invite New Member
              </label>
              <input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="text-white border-none rounded-md px-3.5 py-[9px] font-semibold cursor-pointer h-[38px] text-sm bg-genie-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
